// Pure helpers for the manual-audio result player.
//
// Kept free of React/DOM so the time formatting, source selection, and
// waveform reduction can be unit-tested and reused. The player component owns
// all the playback side effects.

/**
 * The playable audio source for a manual-audio measurement.
 * measurement-service exposes the Firebase Storage download URL as
 * `audioDownloadURL` (audioPath is a storage path and is not directly
 * playable), so we only ever read that field.
 *
 * @param {object|null} data measurement document
 * @returns {string|null}
 */
export function pickManualAudioSource(data) {
  if (!data || typeof data !== "object") return null;
  const url = data.audioDownloadURL;
  return typeof url === "string" && url.trim().length > 0 ? url.trim() : null;
}

/**
 * A duration hint (seconds) recorded by the pod at encode time. Used to show
 * the clip length immediately, before the browser has read the media's own
 * metadata — which for WebM/Opus captures can be missing until first seek.
 *
 * @param {object|null} data measurement document
 * @returns {number|null}
 */
export function pickManualAudioDurationHint(data) {
  const raw = data?.recordingMeta?.encoded?.durationSec;
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0
    ? raw
    : null;
}

/**
 * Format a number of seconds as an Apple Voice Memos style clock ("m:ss").
 *
 * @param {number} seconds
 * @returns {string}
 */
export function formatClock(seconds) {
  const total =
    Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const minutes = Math.floor(total / 60);
  const remaining = total % 60;
  return `${minutes}:${String(remaining).padStart(2, "0")}`;
}

/**
 * Format the time remaining as a negative clock ("−m:ss"), matching the
 * right-hand label in the Voice Memos player. Uses a true minus sign (U+2212).
 *
 * @param {number} current elapsed seconds
 * @param {number} duration total seconds
 * @returns {string}
 */
export function formatRemaining(current, duration) {
  const total = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const elapsed = Number.isFinite(current) && current > 0 ? current : 0;
  const remaining = Math.max(0, total - elapsed);
  return `−${formatClock(remaining)}`;
}

/**
 * Reduce raw PCM samples to `bucketCount` normalised peaks in [0, 1] for a
 * waveform display. Each bucket takes the maximum absolute amplitude in its
 * slice, then the whole set is normalised by the global peak so the tallest
 * bar reaches full height.
 *
 * @param {ArrayLike<number>} samples channel PCM data
 * @param {number} bucketCount number of bars to produce
 * @returns {number[]}
 */
export function computeWaveformPeaks(samples, bucketCount) {
  if (!samples || !samples.length || !Number.isFinite(bucketCount)) return [];
  const bars = Math.max(1, Math.floor(bucketCount));
  const bucketSize = Math.max(1, Math.floor(samples.length / bars));
  const peaks = new Array(bars);
  let max = 0;

  for (let i = 0; i < bars; i += 1) {
    const start = i * bucketSize;
    const end = Math.min(start + bucketSize, samples.length);
    let peak = 0;
    for (let j = start; j < end; j += 1) {
      const value = Math.abs(samples[j]);
      if (value > peak) peak = value;
    }
    peaks[i] = peak;
    if (peak > max) max = peak;
  }

  if (max <= 0) return peaks.map(() => 0);
  return peaks.map((peak) => peak / max);
}

/**
 * Clamp a seek target to the playable range.
 *
 * @param {number} value target seconds
 * @param {number} duration total seconds
 * @returns {number}
 */
export function clampSeekTime(value, duration) {
  const total = Number.isFinite(duration) && duration > 0 ? duration : 0;
  if (!Number.isFinite(value) || value < 0) return 0;
  if (value > total) return total;
  return value;
}
